import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import BrokerageSupportSection from "../referral-links/BrokerageSupportSection";

/**
 * Props:
 * - portfolioData: the full object you pasted (with { portfolio: {...} })
 * - fileName?: optional pdf file name
 */
export default function PortfolioReport({ portfolioData, fileName = "portfolio-report.pdf" }) {
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const p = portfolioData?.portfolio;

  const totals = useMemo(() => {
    const allocTotal = (p?.allocation || []).reduce((sum, a) => sum + (Number(a.percent) || 0), 0);
    const initialTotal = (p?.initialPlan || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const monthlyTotal = (p?.monthlyPlan || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    return { allocTotal, initialTotal, monthlyTotal };
  }, [p]);

  const formatMoney = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
      : "";

  async function handleDownloadPDF() {
  if (!reportRef.current) return;

  setDownloading(true);

  try {
    const element = reportRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY, // helps if user scrolled
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Optional margins (mm)
    const margin = 8;
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;

    // Scale the image to fit the PDF width
    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // How many pages we need
    let remainingHeight = imgHeight;
    let y = margin;

    // First page
    pdf.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
    remainingHeight -= usableHeight;

    // Next pages: move the same big image upward
    // Add a tiny overlap to reduce “row slicing”
    const overlap = 1.5; // mm
    while (remainingHeight > 0) {
      pdf.addPage();
      y = margin - (imgHeight - remainingHeight) + overlap;
      pdf.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
      remainingHeight -= usableHeight;
    }

    pdf.save(fileName);
  } catch (err) {
    console.error("PDF download failed:", err);
    alert("Sorry — PDF export failed. Check console for details.");
  } finally {
    setDownloading(false);
  }
}


  if (!p) {
    return (
      <div className="alert alert-warning">
        Portfolio data missing. Pass in the object as <code>{"{ portfolio: {...} }"}</code>.
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Header / Actions */}
      <div className="d-flex w-100 gap-2 mb-3">
        <div className="d-flex w-100 gap-2">
          <button className="btn btn-primary" style={{marginLeft: "auto"}} onClick={handleDownloadPDF} disabled={downloading}>
            {downloading ? "Generating PDF..." : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Report Body (this is what we export to PDF) */}
      <div ref={reportRef} className="bg-white p-3 p-md-4 border rounded" style={{ paddingBottom: 40 }}>
        <div>
          <h2 className="mb-1">{p.investorType}</h2>
          <div className="text-muted">AI-generated portfolio report</div>
        </div>
        {/* Top Summary */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-8">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-2">Summary</h5>
                <p className="card-text mb-0">{p.summary}</p>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title mb-2">Rebalance</h5>
                <div className="mb-2">
                  <div className="text-muted small">Frequency</div>
                  <div className="fw-semibold">{p.rebalance?.frequency}</div>
                </div>
                <div>
                  <div className="text-muted small">Rule</div>
                  <div className="fw-semibold">{p.rebalance?.rule}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Allocation */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 className="card-title mb-0">Target Allocation</h5>
              <span className={`badge ${totals.allocTotal === 100 ? "bg-success" : "bg-warning text-dark"}`}>
                Total: {totals.allocTotal}%
              </span>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Name</th>
                    <th style={{ width: 90 }}>Ticker</th>
                    <th style={{ width: 140 }}>Role</th>
                    <th style={{ width: 90 }}>Type</th>
                    <th style={{ width: 90 }}>Percent</th>
                    <th>Allocation Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {p.allocation?.map((a) => (
                    <tr key={a.ticker}>
                      <td className="fw-semibold">{a.name}</td>
                      <td className="fw-semibold">{a.ticker}</td>
                      <td>{a.role}</td>
                      <td>
                        <span className={`badge ${a.kind === "ETF" ? "bg-info text-dark" : "bg-secondary"}`}>
                          {a.kind}
                        </span>
                      </td>
                      <td>{a.percent}%</td>
                      <td>
                        <div className="progress" style={{ height: 10 }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${a.percent}%` }}
                            aria-valuenow={a.percent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expanded explanations */}
            <div className="mt-3">
  <h6 className="mb-3">Holdings Details</h6>

  <div className="row g-3">
    {p.allocation?.map((a) => (
      <div key={a.ticker} className="col-12">
        <div className="card h-100 shadow-sm border-0">
          <div className="card-body">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold fs-5">{a.name}</span> | <span className="fw-bold fs-5">{a.ticker}</span>

                  <span className={`badge ${
                    a.kind === "ETF"
                      ? "bg-primary-subtle text-primary border"
                      : "bg-secondary-subtle text-secondary border"
                  }`}>
                    {a.kind}
                  </span>

                  <span className="text-muted small">
                    {a.percent}% • {a.role}
                  </span>
                </div>
              </div>
            </div>

            <hr />

            {/* Why */}
            <div className="mb-3">
              <div className="text-uppercase small text-muted fw-semibold mb-1">
                Why it's included
              </div>
              <div>{a.why}</div>
            </div>

            {/* Description */}
            <div>
              <div className="text-uppercase small text-muted fw-semibold mb-1">
                What it is
              </div>
              <div>{a.description}</div>
            </div>

          </div>
        </div>
      </div>
    ))}
  </div>
</div>

          </div>
        </div>

        {/* Initial + Monthly Plans */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="card-title mb-0">Initial Plan</h5>
                  <span className="badge bg-light text-dark border">
                    Total: {formatMoney(totals.initialTotal)}
                  </span>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Ticker</th>
                        <th style={{ width: 120 }}>Amount</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.initialPlan?.map((x) => (
                        <tr key={x.ticker}>
                          <td className="fw-semibold">{x.ticker}</td>
                          <td>{formatMoney(x.amount)}</td>
                          <td className="text-muted">{x.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Notes */}
                <div className="mt-2">
                  {p.initialPlan?.map((x) =>
                    x.note ? (
                      <div key={`${x.ticker}-note`} className="small text-muted mb-1">
                        <span className="fw-semibold">{x.ticker}:</span> {x.note}
                      </div>
                    ) : null
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 className="card-title mb-0">Monthly Plan</h5>
                  <span className="badge bg-light text-dark border">
                    Total: {formatMoney(totals.monthlyTotal)}
                  </span>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>Ticker</th>
                        <th style={{ width: 120 }}>Amount</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.monthlyPlan?.map((x) => (
                        <tr key={x.ticker}>
                          <td className="fw-semibold">{x.ticker}</td>
                          <td>{formatMoney(x.amount)}</td>
                          <td className="text-muted">{x.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="alert alert-secondary small mb-0">
                  Tip: Automate these buys + reinvest dividends (DRIP) to reduce “decision fatigue.”
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Behavior Warning */}
        {p.behaviorWarning ? (
          <div className="alert alert-warning mb-3">
            <div className="fw-semibold mb-1">Behavior Warning</div>
            <div>{p.behaviorWarning}</div>
          </div>
        ) : null}

        {/* Next Steps + Risks */}
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">Next Steps</h5>
                <ul className="mb-0">
                  {p.nextSteps?.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="card-title">Risks</h5>
                <ul className="mb-0">
                  {p.risks?.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <BrokerageSupportSection referralLink={"https://join.robinhood.com/dominim1157"} />
        </div>
        
        

        {/* Disclaimer */}
        {p.disclaimer ? (
          <div className="mt-3 text-muted small">
            <hr />
            <div className="fw-semibold">Disclaimer</div>
            <div>{p.disclaimer}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
